import { ModuleWithProviders, NgModule, SkipSelf, Optional, InjectionToken } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TranslationService, FormatterService, DateFormatterService } from '@cemex-core/angular-localization-v7';
import { CountlyService } from '@cemex-core/helpers-v1';
import { CountryConfigService, ProjectSettings } from '@cemex-core/angular-services-v8';
import { Broadcaster } from '@cemex-core/events-v7';
import { RolesService } from './services/roles.service';
import { SharedDataService } from './services/sharedData.service';
import { StoredData } from './services/storedData.service';
import { CountryConfigurationService } from './services/ipc/CountryConfiguration.service';
import { CustomerConfigurationService } from './services/ipc/CustomerConfiguration.service';
import { DateConverterService } from './services/helpers/dateConverter.service';
import { EndpointService } from './services/helpers/endpoint.service';
import { IAppConfigurationOptions } from '../cemexng2components/components/app-configuration/models/app-configuration-options';
import { AppConfigurationService } from '../cemexng2components/components/app-configuration/services/ipc-app-configuration.service';
import configurations from '../configurations';
import metadata from '../metadata'

export function _window() { return window; }
export function _configurations() { return configurations; }

export type Environment = 'Development'|'Quality'|'Production';

export function _environment(): Environment {
    const hostname = _window().location.hostname.toLowerCase();
    if (hostname === "localhost" || hostname.startsWith("dev2")) {
        return "Development";
    }
    if (hostname.startsWith("qa")) {
        return "Quality";
    }
    return "Production";
}

export function _getAppVersion() {
  return metadata.version;
}

export function _getBuildDate() {
  return metadata.buildTimestamp;
}

export function _getBuildCommit() {
  return metadata.commit;
}

export const hostEnvironment: any = _environment();
export const appVersion: string = _getAppVersion();
export const WINDOW_TOKEN = new InjectionToken<{}>('window object');
export const ENVIRONMENT_TOKEN = new InjectionToken<Environment>('Environment string: Development | Quality | Production');
export const APP_VERSION_TOKEN = new InjectionToken<string>('App Version string');
export const APP_BUILD_DATE_TOKEN = new InjectionToken<string>('App Build Date string');
export const APP_BUILD_COMMIT_TOKEN = new InjectionToken<string>('App Build Commit string');

@NgModule({
    imports: [CommonModule]
})
export class SingletonsModule {

    constructor(@Optional() @SkipSelf() parentModule: SingletonsModule) {
        if (parentModule) {
            throw new Error('SingletonsModule is already loaded. Import it in the AppModule only');
        }
    }

    static forRoot(): ModuleWithProviders<SingletonsModule> {
        return {
            ngModule: SingletonsModule,
            providers: [
                {
                    provide: WINDOW_TOKEN,
                    useFactory: _window
                },
                {
                    provide: ENVIRONMENT_TOKEN,
                    useFactory: _environment
                },
                {
                    provide: APP_VERSION_TOKEN,
                    useFactory: _getAppVersion
                },
                {
                    provide: APP_BUILD_DATE_TOKEN,
                    useFactory: _getBuildDate
                },
                {
                    provide: APP_BUILD_COMMIT_TOKEN,
                    useFactory: _getBuildCommit
                },
                {
                    provide: 'AppConfigurationOptions',
                    useValue: {
                        applicationCode: 'INVOICE_PAYMENTS',
                        hostname: '',
                        environment: hostEnvironment,
                        releaseVersion: appVersion,
                        useLocalManifest: false,
                        productPath: '/',
                        loadConfigurations: _configurations,
                    } as IAppConfigurationOptions
                },
                {
                    provide: 'WINDOW',
                    useFactory: _window
                },
                AppConfigurationService,
                TranslationService,
                FormatterService,
                DateFormatterService,
                CountlyService,
                RolesService,
                SharedDataService,
                ProjectSettings,
                Broadcaster,
                CountryConfigService,
                StoredData,
                DateConverterService,
                EndpointService,
                CountryConfigurationService,
                CustomerConfigurationService,
            ]
        };
    }
}
